import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
function isAndroid() { return /Android/i.test(navigator.userAgent); }
function isSafari() { return /^((?!chrome|android).)*safari/i.test(navigator.userAgent); }
function isChrome() { return /Chrome/i.test(navigator.userAgent) && !/Edge/i.test(navigator.userAgent); }

export default function VoiceNoteButton({ onTranscript, disabled, accentColor = '#0c2340' }) {
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState('idle'); // idle, recording, paused, processing
  const [showInstruction, setShowInstruction] = useState(false);
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [seconds, setSeconds] = useState(0);
  const recognitionRef = useRef(null);
  const fullTranscriptRef = useRef('');
  const timerRef = useRef(null);

  const supported = !!SpeechRecognition;

  // Timer
  useEffect(() => {
    if (state === 'recording') {
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [state]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  async function processTranscript(text) {
    setState('processing');
    try {
      const { data } = await api.post('/user/polish-voice-text', { text });
      onTranscript(data.polished || text);
    } catch {
      onTranscript(text);
    }
    setState('idle');
    setLiveText('');
    setSeconds(0);
  }

  const startRecognition = useCallback(() => {
    if (!supported) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      fullTranscriptRef.current = final;
      setLiveText((final + interim).trim());
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        // Mic permission denied — show help
        setState('idle');
        setShowPermissionHelp(true);
        return;
      }
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        console.error('Speech recognition error:', event.error);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still in recording state (silence timeout)
      if (recognitionRef.current && document.querySelector('[data-voice-state="recording"]')) {
        try { recognitionRef.current.start(); } catch {}
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      console.error('Failed to start recognition:', e);
      setState('idle');
    }
  }, [supported]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check permission ONLY when user taps mic — never on load
  async function handleMicClick() {
    if (disabled || !supported) return;

    // First time — show instruction
    if (!user?.voiceInstructionSeen) {
      setShowInstruction(true);
      return;
    }

    // Check mic permission state before starting
    // This does NOT trigger the browser prompt — it just reads the current state
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        if (result.state === 'denied') {
          setShowPermissionHelp(true);
          return;
        }
        // 'prompt' or 'granted' — proceed. The browser will show its own
        // permission dialog for 'prompt', which is fine because the user
        // just explicitly tapped the mic button.
      }
    } catch {
      // permissions.query not supported (e.g. Safari) — just proceed,
      // SpeechRecognition will handle the permission prompt itself
    }

    // Start recording
    fullTranscriptRef.current = '';
    setLiveText('');
    setSeconds(0);
    setState('recording');
    startRecognition();
  }

  function handlePause() {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setState('paused');
  }

  function handleResume() {
    setState('recording');
    startRecognition();
  }

  function handleStop() {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    const text = fullTranscriptRef.current.trim() || liveText.trim();
    if (text) {
      processTranscript(text);
    } else {
      setState('idle');
      setLiveText('');
      setSeconds(0);
    }
  }

  function handleCancel() {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    setState('idle');
    setLiveText('');
    setSeconds(0);
    fullTranscriptRef.current = '';
  }

  async function dismissInstruction() {
    setShowInstruction(false);
    try {
      await api.post('/user/voice-instruction-seen');
      refreshUser();
    } catch {}
  }

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
      }
    };
  }, []);

  if (!supported) return null;

  const isActive = state === 'recording' || state === 'paused' || state === 'processing';

  return (
    <>
      {/* ── Recording overlay ── */}
      {isActive && (
        <div style={styles.overlay} data-voice-state={state}>
          <div style={styles.overlayCard}>
            <div style={styles.overlayHeader}>
              <span style={{
                ...styles.statusBadge,
                background: state === 'recording' ? '#fef2f2' : state === 'paused' ? '#fffbeb' : '#f3f4f6',
                color: state === 'recording' ? '#dc2626' : state === 'paused' ? '#d97706' : '#6b7280',
              }}>
                {state === 'recording' && <span style={styles.recordingDot} />}
                {state === 'recording' ? 'Recording' : state === 'paused' ? 'Paused' : 'Processing...'}
              </span>
              {state !== 'processing' && (
                <span style={styles.timer}>{formatTime(seconds)}</span>
              )}
            </div>

            {state !== 'processing' ? (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                <div style={{ ...styles.micCircle, background: state === 'recording' ? accentColor : '#d1d5db', position: 'relative' }}>
                  {state === 'recording' && <div style={{ ...styles.pulseRing, borderColor: accentColor }} />}
                  <MicIcon color="#fff" size={28} />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                <div style={styles.spinSmall} />
              </div>
            )}

            <div style={styles.liveTextBox}>
              {liveText ? (
                <p style={styles.liveText}>{liveText}</p>
              ) : (
                <p style={{ ...styles.liveText, color: '#9ca3af', fontStyle: 'italic' }}>
                  {state === 'recording' ? 'Listening... start speaking' : state === 'paused' ? 'Paused. Tap resume to continue.' : 'Polishing your text...'}
                </p>
              )}
            </div>

            {state !== 'processing' && (
              <div style={styles.controls}>
                <button onClick={handleCancel} style={styles.controlBtn}>
                  <CancelIcon />
                  <span style={styles.controlLabel}>Cancel</span>
                </button>
                {state === 'recording' ? (
                  <button onClick={handlePause} style={styles.controlBtn}>
                    <PauseIcon />
                    <span style={styles.controlLabel}>Pause</span>
                  </button>
                ) : (
                  <button onClick={handleResume} style={styles.controlBtn}>
                    <ResumeIcon color={accentColor} />
                    <span style={styles.controlLabel}>Resume</span>
                  </button>
                )}
                <button onClick={handleStop} style={styles.controlBtn}>
                  <DoneIcon />
                  <span style={styles.controlLabel}>Done</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Instruction modal ── */}
      {showInstruction && (
        <div style={styles.modalBackdrop} onClick={dismissInstruction}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{ ...styles.instructionIcon, background: accentColor }}>
              <MicIcon color="#fff" size={24} />
            </div>
            <h3 style={styles.modalTitle}>Voice Input</h3>
            <p style={styles.modalText}>
              Tap the microphone to start recording. Speak your answer naturally.
            </p>
            <p style={styles.modalText}>
              You can <strong>pause and resume</strong> anytime. When done, tap <strong>Done</strong> and your speech will be cleaned up for grammar and added to the text box.
            </p>
            <p style={styles.modalText}>
              You can record multiple times — each recording appends to your existing text.
            </p>
            <button style={{ ...styles.modalBtn, background: accentColor }} onClick={dismissInstruction}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Permission denied help modal ── */}
      {showPermissionHelp && (
        <div style={styles.modalBackdrop} onClick={() => setShowPermissionHelp(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{ ...styles.instructionIcon, background: '#dc2626' }}>
              <MicOffIcon />
            </div>
            <h3 style={styles.modalTitle}>Microphone Access Needed</h3>
            <p style={styles.modalText}>
              To use voice input, please allow microphone access for this website. Here is how:
            </p>
            <div style={styles.permissionSteps}>
              <PermissionSteps />
            </div>
            <p style={{ ...styles.modalText, fontSize: 12, color: '#9ca3af', marginTop: 12, marginBottom: 0 }}>
              After allowing access, come back and tap the microphone again.
            </p>
            <button
              style={{ ...styles.modalBtn, background: accentColor }}
              onClick={() => setShowPermissionHelp(false)}
            >
              OK, I will allow access
            </button>
          </div>
        </div>
      )}

      {/* ── Mic trigger button ── */}
      <button
        type="button"
        onClick={handleMicClick}
        style={{
          ...styles.btn,
          background: disabled ? 'transparent' : `${accentColor}0D`,
          borderColor: disabled ? 'rgba(12,35,64,0.12)' : `${accentColor}30`,
          opacity: disabled ? 0.35 : 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
        disabled={disabled || isActive}
        title="Tap to record voice"
      >
        <MicIcon color={disabled ? '#9ca3af' : accentColor} size={18} />
      </button>

      <style>{`
        @keyframes voice-pulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes voice-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes voice-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </>
  );
}

/* ── Permission steps — device-specific ── */

function PermissionSteps() {
  if (isIOS()) {
    if (isSafari()) {
      return (
        <ol style={stepListStyle}>
          <li>Open your iPhone <strong>Settings</strong> app</li>
          <li>Scroll down and tap <strong>Safari</strong></li>
          <li>Tap <strong>Microphone</strong> under "Settings for Websites"</li>
          <li>Select <strong>Allow</strong></li>
          <li>Come back to this page and try again</li>
        </ol>
      );
    }
    // Chrome on iOS
    return (
      <ol style={stepListStyle}>
        <li>Open your iPhone <strong>Settings</strong> app</li>
        <li>Scroll down and tap <strong>Chrome</strong></li>
        <li>Make sure <strong>Microphone</strong> is turned on</li>
        <li>Come back to this page and try again</li>
      </ol>
    );
  }

  if (isAndroid()) {
    return (
      <ol style={stepListStyle}>
        <li>Tap the <strong>lock icon</strong> in the address bar (or the three dots menu)</li>
        <li>Tap <strong>Permissions</strong> or <strong>Site settings</strong></li>
        <li>Find <strong>Microphone</strong> and set it to <strong>Allow</strong></li>
        <li>Reload this page and try again</li>
      </ol>
    );
  }

  // Desktop
  if (isChrome()) {
    return (
      <ol style={stepListStyle}>
        <li>Click the <strong>lock icon</strong> (or tune icon) in the address bar</li>
        <li>Find <strong>Microphone</strong> in the list</li>
        <li>Change it to <strong>Allow</strong></li>
        <li>Reload this page and try again</li>
      </ol>
    );
  }

  // Generic fallback
  return (
    <ol style={stepListStyle}>
      <li>Look for a <strong>camera/microphone icon</strong> in your browser's address bar</li>
      <li>Click it and select <strong>Allow</strong> for microphone</li>
      <li>If you do not see it, go to your browser's <strong>Settings &gt; Privacy &gt; Site Settings &gt; Microphone</strong></li>
      <li>Allow microphone for this website, then reload and try again</li>
    </ol>
  );
}

const stepListStyle = {
  textAlign: 'left',
  paddingLeft: 20,
  margin: 0,
  fontSize: 13,
  color: '#374151',
  lineHeight: 1.7,
  fontFamily: "'DM Sans', sans-serif",
};

/* ── Icons ── */

function MicIcon({ color = '#0c2340', size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.49-.35 2.17" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="#d97706" stroke="none">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function ResumeIcon({ color = '#0c2340' }) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill={color} stroke="none">
      <polygon points="6,4 20,12 6,20" />
    </svg>
  );
}

function DoneIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ── Styles ── */

const styles = {
  btn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: '0 12px 24px',
    backdropFilter: 'blur(4px)',
  },
  overlayCard: {
    background: '#fff',
    borderRadius: 24,
    padding: '20px 20px 24px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 -4px 40px rgba(0,0,0,0.2)',
  },
  overlayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#dc2626',
    animation: 'voice-blink 1s ease-in-out infinite',
  },
  timer: {
    fontSize: 14,
    fontWeight: 700,
    color: '#374151',
    fontFamily: "'DM Sans', sans-serif",
    fontVariantNumeric: 'tabular-nums',
  },
  micCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  pulseRing: {
    position: 'absolute',
    inset: -8,
    borderRadius: '50%',
    border: '3px solid',
    animation: 'voice-pulse 1.5s ease-out infinite',
    pointerEvents: 'none',
  },
  liveTextBox: {
    background: '#f9fafb',
    borderRadius: 12,
    padding: '12px 14px',
    minHeight: 60,
    maxHeight: 140,
    overflowY: 'auto',
    marginBottom: 16,
  },
  liveText: {
    color: '#1a2f4a',
    fontSize: 14,
    lineHeight: 1.6,
    margin: 0,
    fontFamily: "'DM Sans', sans-serif",
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  controlBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: 12,
    transition: 'background 0.15s',
  },
  controlLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    fontFamily: "'DM Sans', sans-serif",
  },
  spinSmall: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '3px solid #e5e7eb',
    borderTopColor: '#0c2340',
    animation: 'voice-spin 0.8s linear infinite',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    padding: '32px 28px',
    maxWidth: 360,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  },
  instructionIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  modalTitle: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    color: '#0c2340',
    margin: '0 0 10px',
  },
  modalText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 1.6,
    margin: '0 0 12px',
    fontFamily: "'DM Sans', sans-serif",
  },
  modalBtn: {
    width: '100%',
    padding: '12px 24px',
    borderRadius: 12,
    border: 'none',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    marginTop: 16,
  },
  permissionSteps: {
    background: '#f9fafb',
    borderRadius: 12,
    padding: '14px 16px',
    border: '1px solid #e5e7eb',
  },
};
